import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { Observable, catchError, map, of, tap } from "rxjs";
import { Country, CountriesService } from "src/app/shared/countries/countries.service";
import { LocalStorageService } from "../local-storage.service";
import { AppConfigType } from "../models/appConfig.model";
import { enabledUnitsOf } from "./app-config";
import { OrgAppConfig, OrganizationBootstrap, OrganizationListItem } from "./bootstrap.model";
import { environment } from "../../../environments/environment";

@Injectable({
    providedIn: "root",
})
export class BootstrapService {
    private readonly http = inject(HttpClient);
    private readonly storage = inject(LocalStorageService);
    private readonly countries = inject(CountriesService);
    private refreshedFor: number | null = null;

    readonly bootstrap = signal<OrganizationBootstrap | null>(this.stored());

    readonly flightOperationNames = computed(() => this.namesOf(this.bootstrap()?.flightOperations));
    readonly flightSpecificationNames = computed(() => this.namesOf(this.bootstrap()?.flightSpecifications));
    readonly flightTypeNoteNames = computed(() => this.namesOf(this.bootstrap()?.flightTypeNotes));
    readonly pilotRoles = computed(() => this.bootstrap()?.pilotRoles ?? []);
    readonly pilotRoleNames = computed(() => new Map(this.pilotRoles().map((role) => [this.abbreviationKey(role.abbreviation), role.name.trim()])));
    readonly organization = computed(() => this.bootstrap()?.organization ?? null);
    /** The time zone the organization's flights are kept in; the browser's until the bootstrap knows it. */
    readonly organizationTimeZone = computed(() => this.organization()?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    readonly crewRoles = computed(() => this.bootstrap()?.crewRoles ?? []);
    readonly crews = computed(() => this.bootstrap()?.crews ?? []);
    readonly pilots = computed(() => this.bootstrap()?.pilots ?? []);
    readonly aircraft = computed(() => this.bootstrap()?.aircraft ?? []);
    readonly aircraftById = computed(() => new Map(this.aircraft().map((aircraft) => [aircraft.id, aircraft])));
    readonly flightTypes = computed(() => this.bootstrap()?.flightTypes ?? []);
    readonly appConfigs = computed(() => this.bootstrap()?.appConfigs ?? []);
    readonly maxTankCapacityEnabled = computed(() => this.configOf(AppConfigType.maxTankCapacity)?.enabled === true);
    readonly oilUnits = computed(() => enabledUnitsOf(this.configOf(AppConfigType.oilUnits), ["gr", "ml"]));
    readonly fuelUnits = computed(() => enabledUnitsOf(this.configOf(AppConfigType.fuelUnits), ["kg", "lt"]));

    countryOf(code: string | null | undefined): Country | null {
        return code ? (this.countries.byCode()[code.toUpperCase()] ?? null) : null;
    }

    readonly airportsById = computed(() => new Map((this.bootstrap()?.airports ?? []).map((airport) => [airport.id, airport])));

    ensure(): Observable<boolean> {
        const stored = this.stored();
        if (stored) {
            this.bootstrap.set(stored);
            return of(true);
        }
        // Without the data the pages show ids instead of names, which is still better than no page.
        return this.load().pipe(
            map(() => true),
            catchError(() => of(true)),
        );
    }

    refresh(): void {
        const organizationId = this.organizationId();
        if (!organizationId || this.refreshedFor === organizationId) return;
        this.load().subscribe({ error: () => undefined });
    }

    private load(): Observable<OrganizationBootstrap> {
        const organizationId = this.organizationId();
        return this.http.get<OrganizationBootstrap>(new URL("../v2/frontend/organization/bootstrap", environment.serverUrl).href).pipe(
            tap((bootstrap) => {
                this.storage.setAppLocalStorage("bootstrap", { ...bootstrap, organizationId: organizationId });
                this.bootstrap.set(bootstrap);
                this.refreshedFor = organizationId;
            }),
        );
    }

    private stored(): OrganizationBootstrap | null {
        if (!this.storage.appLocalStorageExist()) return null;
        const stored = this.storage.getAppLocalStorageObject().bootstrap;
        return stored && stored.organizationId === this.organizationId() ? stored : null;
    }

    private organizationId(): number | null {
        return this.storage.getUserLocalStorageObject()?.organizationId ?? null;
    }

    configOf(type: AppConfigType): OrgAppConfig | undefined {
        return this.appConfigs().find((config) => config.type === type);
    }

    abbreviationKey(abbreviation: unknown): string {
        return String(abbreviation ?? "")
            .trim()
            .toUpperCase();
    }

    private namesOf(items: OrganizationListItem[] | undefined): Map<number, string> {
        return new Map((items ?? []).map((item) => [item.id, item.name]));
    }
}
